import type {
  SolarProposalSummaryDto,
  SolarSummaryRequirementStep,
} from "~/api/dto/solar-proposal-summary.dto"

import {
  SOLAR_CREDIT_HUMAN_FINANCE_TYPE,
  SOLAR_LIGHTREACH_FINANCE_TYPE,
  SOLAR_SUNGAGE_FINANCE_TYPE,
} from "../solar-finance-partner"
import { solarProposalPath } from "../solar-setup-paths"

/** V1's Summary step ids, which name the URLs as V1's do, and Loan Product. */
export type SolarSummaryStepId =
  | `energy-consumption`
  | `equipment-selection`
  | `funding-selection`
  | `loan-product`
  | `utility-information`

export type SolarSummaryStep = {
  readonly id: SolarSummaryStepId
  /** The readiness step whose requirements this step meets. */
  readonly requirementStep: SolarSummaryRequirementStep
} & (
  | {
      /** V1's i18n key for the step title. */
      readonly titleKey:
        | `energyConsumption`
        | `equipmentSelection`
        | `fundingSelection`
        | `utilityInformation`
    }
  /** A step V1 never had, so no V1 i18n key. */
  | { readonly title: string }
)

/**
 * V1's four steps, in V1's order (`summary-steps.ts`), then Loan Product
 * (Credit Human's term, Sungage's product or LightReach's offer): its
 * selection needs the plan Equipment's save stores.
 */
export const SOLAR_SUMMARY_STEPS: readonly SolarSummaryStep[] = [
  {
    id: `utility-information`,
    titleKey: `utilityInformation`,
    requirementStep: `utility`,
  },
  {
    id: `energy-consumption`,
    titleKey: `energyConsumption`,
    requirementStep: `consumption`,
  },
  {
    id: `funding-selection`,
    titleKey: `fundingSelection`,
    requirementStep: `funding`,
  },
  {
    id: `equipment-selection`,
    titleKey: `equipmentSelection`,
    requirementStep: `equipment`,
  },
  {
    id: `loan-product`,
    title: `Loan Product`,
    requirementStep: `loanProduct`,
  },
]

/**
 * The plans whose Summary has a Loan Product step: Credit Human's loan term,
 * LightReach's offer, and a product of Sungage's funding group.
 */
const LOAN_PRODUCT_STEP_PLANS: ReadonlySet<string> = new Set([
  SOLAR_CREDIT_HUMAN_FINANCE_TYPE,
  SOLAR_LIGHTREACH_FINANCE_TYPE,
  SOLAR_SUNGAGE_FINANCE_TYPE,
])

/**
 * The steps this proposal shows: Loan Product only for a Credit Human plan
 * (its loan term), a Sungage plan (its group's product) or a LightReach plan
 * (its offer). A partner drafted on
 * Funding decides until Equipment saves it; with nothing drafted, the
 * readiness read lists the requirement only for a stored plan of either.
 */
export const visibleSummarySteps = ({
  summary,
  draftedFinanceType,
}: {
  summary: SolarProposalSummaryDto
  draftedFinanceType: string | undefined
}): readonly SolarSummaryStep[] => {
  const takesLoanProduct =
    draftedFinanceType !== undefined
      ? LOAN_PRODUCT_STEP_PLANS.has(draftedFinanceType)
      : summary.requirements.some(
          (requirement) => requirement.step === `loanProduct`,
        )
  return SOLAR_SUMMARY_STEPS.filter(
    (step) => step.id !== `loan-product` || takesLoanProduct,
  )
}

export const solarSummaryPath = (proposalId: string): string =>
  `${solarProposalPath(proposalId)}/summary`

export const solarSummaryStepPath = (
  proposalId: string,
  step: SolarSummaryStepId,
): string => `${solarSummaryPath(proposalId)}/${step}`

/** Green Button Data opens over the Energy Consumption step. */
export const solarSummaryGreenButtonPath = (proposalId: string): string =>
  `${solarSummaryStepPath(proposalId, `energy-consumption`)}/greenbutton`

/** What the path names under `/summary`: null off the Summary. A trailing
 * slash still counts; an unknown step reads as `step: null`. */
export const solarSummaryRouteFromPath = (
  pathname: string,
  proposalId: string,
): { step: SolarSummaryStepId | null; greenButton: boolean } | null => {
  const path = pathname.replace(/\/+$/, ``)
  const summaryPath = solarSummaryPath(proposalId)
  if (path === summaryPath) {
    return { step: null, greenButton: false }
  }
  if (!path.startsWith(`${summaryPath}/`)) {
    return null
  }
  const [stepId, ...rest] = path.slice(summaryPath.length + 1).split(`/`)
  const step = SOLAR_SUMMARY_STEPS.find((entry) => entry.id === stepId)
  if (!step) {
    return { step: null, greenButton: false }
  }
  const greenButton =
    step.id === `energy-consumption` &&
    rest.length === 1 &&
    rest[0] === `greenbutton`
  return rest.length === 0 || greenButton
    ? { step: step.id, greenButton }
    : { step: null, greenButton: false }
}

/** Whether the path is under `/solar-proposal/:id/summary`. */
export const isSolarSummaryPath = (
  pathname: string,
  proposalId: string,
): boolean => solarSummaryRouteFromPath(pathname, proposalId) !== null

/** Each step's requirements met, by the server's readiness read. */
export const summaryStepsMet = (
  summary: SolarProposalSummaryDto,
): Record<SolarSummaryStepId, boolean> => {
  const metFor = (step: SolarSummaryRequirementStep): boolean =>
    summary.requirements
      .filter((requirement) => requirement.step === step)
      .every((requirement) => requirement.met)

  return Object.fromEntries(
    SOLAR_SUMMARY_STEPS.map((step) => [step.id, metFor(step.requirementStep)]),
  ) as Record<SolarSummaryStepId, boolean>
}

export type SolarSummaryStepState = `current` | `done` | `locked` | `open`

/**
 * V1's progress rule: a step is done once its requirements are met and the
 * seller has moved past it this session, and a step opens only after every
 * earlier one is done. Judged over the steps shown; a hidden step is never
 * unlocked.
 */
export const summaryProgress = ({
  steps,
  met,
  continued,
  current,
}: {
  steps: readonly SolarSummaryStep[]
  met: Record<SolarSummaryStepId, boolean>
  continued: ReadonlySet<SolarSummaryStepId>
  current: SolarSummaryStepId | null
}): {
  states: Record<SolarSummaryStepId, SolarSummaryStepState>
  /** The first step not done: where the Summary sends a locked link. The last step once all are. */
  resume: SolarSummaryStepId
  isUnlocked: (step: SolarSummaryStepId) => boolean
} => {
  const done = (step: SolarSummaryStepId): boolean =>
    met[step] && continued.has(step)
  const isUnlocked = (step: SolarSummaryStepId): boolean => {
    const index = steps.findIndex((entry) => entry.id === step)
    return index >= 0 && steps.slice(0, index).every((entry) => done(entry.id))
  }
  const resume = (
    steps.find((entry) => !done(entry.id)) ?? steps[steps.length - 1]
  ).id

  const states = Object.fromEntries(
    steps.map(({ id }) => [
      id,
      id === current
        ? `current`
        : done(id)
          ? `done`
          : isUnlocked(id)
            ? `open`
            : `locked`,
    ]),
  ) as Record<SolarSummaryStepId, SolarSummaryStepState>

  return { states, resume, isUnlocked }
}

/**
 * Battery's VIS-497 rule: an actor who may update an unconfirmed proposal
 * meets the Summary first; a read-only viewer always gets the proposal. Only
 * an explicit null is unconfirmed: a header from a gateway that predates the
 * field (deploy skew, a rollback) never locks editors out of the proposal.
 */
export const isSolarSummaryRequired = (header: {
  viewerCanUpdate: boolean
  summaryConfirmedAt?: string | null
}): boolean => header.viewerCanUpdate && header.summaryConfirmedAt === null

/**
 * Where Confirm & open lands: the proposal path the gate sent the seller from
 * (its query string and hash too), or the proposal itself for anything else,
 * so a crafted router state can't send the seller off the proposal.
 */
const summaryReturnTo = (target: unknown, proposalId: string): string => {
  const proposalPath = solarProposalPath(proposalId)
  if (typeof target !== `string`) {
    return proposalPath
  }
  const path = target.split(/[?#]/)[0]
  const onProposal = path === proposalPath || path.startsWith(`${proposalPath}/`)
  return onProposal && !isSolarSummaryPath(path, proposalId)
    ? target
    : proposalPath
}

/** The `returnTo` a Summary route's router state carries, checked as above. */
export const readSummaryReturnTo = (
  locationState: unknown,
  proposalId: string,
): string =>
  summaryReturnTo(
    (locationState as { returnTo?: unknown } | null)?.returnTo,
    proposalId,
  )
